import { useCallback, useState } from 'react'
import {
  Document,
  IDocumentsStepContext,
  NotRelevant,
} from '../../end-of-year-review/types'

type NotRelevantModalState = Pick<
  Document,
  'categoryCode' | 'provider' | 'name'
> | null

/**
 * useNotRelevantDocuments - Custom hook for managing "not relevant" document workflow
 *
 * Provides functions to check and toggle
 * document "not relevant" status, plus modal state management.
 *
 * @param stepContext - The step context containing documents
 * @param setStepContext - Function to update the step context
 * @returns Object with functions and modal state
 *
 * @example
 * ```tsx
 * const PayrollDocumentComponent = ({ stepContext, setStepContext }) => {
 *   const { isMarkedNotRelevant, handleNotRelevant, notRelevantModal } =
 *     useNotRelevantDocuments(stepContext, setStepContext)
 *
 *   return (
 *     <>
 *       <DocumentUploadCard
 *         notRelevant={isMarkedNotRelevant('formW3', 'Gusto')}
 *         setChecked={() => handleNotRelevant('formW3', 'Gusto')}
 *       />
 *       <NotRelevantModal {...notRelevantModal} />
 *     </>
 *   )
 * }
 * ```
 */
export const useNotRelevantDocuments = (
  stepContext: IDocumentsStepContext | undefined,
  setStepContext: (context: IDocumentsStepContext) => void
) => {
  const [notRelevantModalState, setNotRelevantModalState] =
    useState<NotRelevantModalState>(null)

  /**
   * Handle marking a document as not relevant or removing the marking
   * If document is already marked, removes it from the list
   * If not marked, opens the confirmation modal
   */
  const handleNotRelevant = useCallback(
    (categoryIdentifier: string, provider?: string, documentName?: string) => {
      const notRelevantDoc = stepContext?.documents?.find(
        (doc) =>
          doc.categoryCode === categoryIdentifier &&
          doc.provider === provider &&
          doc.name === documentName
      )

      // If the document is already marked as not relevant, remove it
      if (notRelevantDoc?.notRelevant) {
        if (!stepContext) return

        setStepContext({
          ...stepContext,
          documents: (stepContext.documents ?? []).map((doc) =>
            doc === notRelevantDoc
              ? {
                  ...doc,
                  notRelevant: undefined,
                }
              : doc
          ),
        })
      } else {
        // Otherwise, open the dialog to mark it as not relevant
        setNotRelevantModalState({
          categoryCode: categoryIdentifier,
          provider,
          name: documentName,
        })
      }
    },
    [stepContext, setStepContext]
  )

  const isMarkedNotRelevant = useCallback(
    (categoryIdentifier: string, provider?: string, documentName?: string) =>
      stepContext?.documents?.some(
        (doc) =>
          doc.provider === provider &&
          doc.categoryCode === categoryIdentifier &&
          doc.name === documentName &&
          doc.notRelevant
      ),
    [stepContext?.documents]
  )

  const onNotRelevantModalCancel = useCallback(
    () => setNotRelevantModalState(null),
    []
  )

  const onNotRelevantModalConfirm = useCallback(
    (notRelevant: NotRelevant) => {
      if (notRelevantModalState) {
        const existingDocuments = stepContext?.documents ?? []
        const isTracked = existingDocuments.some(
          (doc) =>
            doc.categoryCode === notRelevantModalState.categoryCode &&
            doc.provider === notRelevantModalState.provider &&
            doc.name === notRelevantModalState.name
        )
        const markedDocument = {
          ...notRelevantModalState,
          notRelevant,
          updatedByUser: true,
        }

        setStepContext({
          ...(stepContext ?? {}),
          documents: isTracked
            ? existingDocuments.map((doc) =>
                doc.categoryCode === notRelevantModalState.categoryCode &&
                doc.provider === notRelevantModalState.provider &&
                doc.name === notRelevantModalState.name
                  ? { ...doc, notRelevant, updatedByUser: true }
                  : doc
              )
            : [...existingDocuments, markedDocument],
        })
        setNotRelevantModalState(null)
      }
    },
    [notRelevantModalState, stepContext, setStepContext]
  )

  return {
    /**
     * Function to check if a document is marked as not relevant
     */
    isMarkedNotRelevant,

    /**
     * Function to handle marking/unmarking a document as not relevant
     */
    handleNotRelevant,

    /**
     * Modal state and handlers for NotRelevantModal component
     */
    notRelevantModal: {
      isOpen: Boolean(notRelevantModalState),
      handleClose: onNotRelevantModalCancel,
      setConfirmNotRelevant: onNotRelevantModalConfirm,
      provider: notRelevantModalState?.provider,
      categoryCode: notRelevantModalState?.categoryCode,
    },
  }
}
